import type { SessionDTO } from "../api";

/**
 * A just-played session, surfaced on Today + the Club page so people can enter
 * scores after the fact. Before a result is recorded it shows a "Record score"
 * CTA (admins or players who were confirmed); once scored it flips to show the
 * recorded result with a link to the full breakdown.
 */
export function RecentlyPlayedCard({
  session,
  isAdmin,
  resultsVisible,
  onRecord,
  onShowResults,
}: {
  session: SessionDTO;
  isAdmin: boolean;
  resultsVisible: boolean;
  onRecord: () => void;
  onShowResults: () => void;
}) {
  const canRecord =
    (isAdmin || session.my_status === "confirmed") && session.format != null;
  const canViewResults = isAdmin || resultsVisible;

  return (
    <div className="flex items-center gap-3 border border-ink/15 rounded-md px-4 py-3 bg-bg">
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold text-sm truncate">
          {session.starts_at_display}
        </div>
        <div className="text-xs text-ink/55 truncate">
          {session.group_name}
          {session.location ? ` · ${session.location}` : ""}
        </div>
      </div>
      {session.has_result ? (
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono text-[10px] uppercase tracking-wider text-success">
            ✓ {session.result_summary ?? "recorded"}
          </span>
          {canViewResults && (
            <button
              onClick={onShowResults}
              className="cs-tap px-3 py-1.5 border border-ink/20 hover:border-ink/40 text-[10px] uppercase tracking-wider font-bold"
            >
              View
            </button>
          )}
          {/* Sessions hold many games (rotating partners), so keep recording
              available even after the first result is in. */}
          {canRecord && (
            <button
              onClick={onRecord}
              className="cs-tap px-3 py-1.5 border border-ink/20 hover:border-ink/40 text-[10px] uppercase tracking-wider font-bold"
            >
              Record another
            </button>
          )}
        </div>
      ) : canRecord ? (
        <button
          onClick={onRecord}
          className="cs-tap px-3 py-1.5 bg-accent text-ink text-[10px] uppercase tracking-wider font-bold shrink-0"
        >
          Record score
        </button>
      ) : (
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink/40 shrink-0">
          Awaiting score
        </span>
      )}
    </div>
  );
}
